import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Briefcase,
  Calendar,
  CheckSquare,
  Eye,
  Filter,
  Mail,
  MapPin,
  Phone,
  Search,
  Send,
  Users,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

// Helper – returns nice location string or fallback
const formatLocation = (locLike) => {
  if (!locLike) return 'Location not specified';
  if (typeof locLike === 'string') return locLike;
  const { city = '', state = '', country = '' } = locLike || {};
  if (city || state || country) {
    return `${city}${state ? ', ' + state : ''}${country ? ', ' + country : ''}`.trim();
  }
  return 'Location not specified';
};

const CandidateSearch = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showBulkRequest, setShowBulkRequest] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    experience: '',
    skills: '',
    education: '',
    availability: '',
    sortBy: '-updatedAt'
  });
  const [jobs, setJobs] = useState([]);
  const [bulkRequestData, setBulkRequestData] = useState({
    title: '',
    jobId: '',
    message: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCandidates: 0,
    limit: 12
  });

  useEffect(() => {
    fetchCandidates();
    fetchJobs();
  }, [filters, pagination.currentPage]);

  useEffect(() => {
    filterCandidates();
  }, [candidates, filters]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        sort: filters.sortBy,
        ...(filters.location && { location: filters.location }),
        ...(filters.experience && { experience: filters.experience }),
        ...(filters.skills && { skills: filters.skills }),
        ...(filters.education && { education: filters.education })
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/recruiters/candidates/search?${params}`
      );

      setCandidates(response.data.candidates || []);
      setPagination({
        ...pagination,
        totalPages: response.data.totalPages || 1,
        totalCandidates: response.data.totalCandidates || 0
      });
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs?limit=100&status=active`
      );
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const filterCandidates = () => {
    let filtered = [...candidates];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(candidate => 
        candidate.firstName?.toLowerCase().includes(searchTerm) ||
        candidate.lastName?.toLowerCase().includes(searchTerm) ||
        candidate.email?.toLowerCase().includes(searchTerm) ||
        candidate.skills?.some(skill => skill.name?.toLowerCase().includes(searchTerm)) ||
        candidate.profile?.bio?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredCandidates(filtered);
  };

  const handleCandidateSelect = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredCandidates.map(candidate => candidate._id));
    }
  };

  const handleBulkRequest = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }

    if (!bulkRequestData.title.trim()) {
      toast.error('Please enter a collection name');
      return;
    }

    if (!bulkRequestData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      console.log('🔥 FRONTEND - Sending bulk request:', {
        candidateIds: selectedCandidates,
        jobId: bulkRequestData.jobId || null,
        message: bulkRequestData.message,
        title: bulkRequestData.title
      });

      const token = localStorage.getItem('recruiter_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/requests`,
        {
          candidateIds: selectedCandidates,
          jobId: bulkRequestData.jobId || null,
          message: bulkRequestData.message,
          title: bulkRequestData.title
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ FRONTEND - Bulk request response:', response.data);

      toast.success(`Bulk request sent to admin for ${selectedCandidates.length} candidates`);
      setSelectedCandidates([]);
      setBulkRequestData({ title: '', jobId: '', message: '' });
      setShowBulkRequest(false);
    } catch (error) {
      console.error('❌ FRONTEND - Error sending bulk request:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(', ') || 
                          'Failed to send bulk request';
      toast.error(errorMessage);
    }
  };

  const calculateMatchScore = (candidate, job) => {
    if (!candidate?.skills || !job?.skills) return 0;
    
    // Handle skills as objects with name property
    const candidateSkills = candidate.skills.map(skill => 
      typeof skill === 'string' ? skill.toLowerCase() : skill.name?.toLowerCase()
    ).filter(Boolean);
    
    const jobSkills = job.skills.map(skill => 
      typeof skill === 'string' ? skill.toLowerCase() : skill.name?.toLowerCase()
    ).filter(Boolean);
    
    const matchingSkills = candidateSkills.filter(skill => 
      jobSkills.some(jobSkill => jobSkill.includes(skill) || skill.includes(jobSkill))
    );
    
    return Math.round((matchingSkills.length / jobSkills.length) * 100);
  };

  const getExperienceLevel = (totalExperience) => {
    if (totalExperience < 2) return 'Entry Level';
    if (totalExperience < 5) return 'Mid Level';
    if (totalExperience < 10) return 'Senior Level';
    return 'Expert Level';
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      experience: '',
      skills: '',
      education: '',
      availability: '',
      sortBy: '-updatedAt'
    });
  };

  const handlePageChange = (page) => {
    setPagination({ ...pagination, currentPage: page });
  };

  if (loading && candidates.length === 0) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Candidate Search</h1>
          <p className="text-gray-600">
            Find and connect with talented candidates for your open positions
          </p>
        </div>
        
        {selectedCandidates.length > 0 && (
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <span className="text-sm text-gray-600">
              {selectedCandidates.length} selected
            </span>
            <button
              onClick={() => setShowBulkRequest(true)}
              className="btn-primary flex items-center"
            >
              <Send className="w-5 h-5 mr-2" />
              Send Request
            </button>
          </div>
        )}
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-8"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, skills, or bio..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4">
            <input
              type="text"
              placeholder="Location"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="input-field min-w-[120px]"
            />

            <select
              value={filters.experience}
              onChange={(e) => handleFilterChange('experience', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="">All Experience</option>
              <option value="0-2">0-2 years</option>
              <option value="2-5">2-5 years</option>
              <option value="5-10">5-10 years</option>
              <option value="10+">10+ years</option>
            </select>

            <input
              type="text"
              placeholder="Skills"
              value={filters.skills}
              onChange={(e) => handleFilterChange('skills', e.target.value)}
              className="input-field min-w-[120px]"
            />

            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="-updatedAt">Recently Updated</option>
              <option value="-createdAt">Recently Joined</option>
              <option value="firstName">Name A-Z</option>
              <option value="-totalExperience">Most Experience</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleSelectAll}
              className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <CheckSquare className="w-4 h-4" />
              <span>
                {selectedCandidates.length === filteredCandidates.length ? 'Deselect All' : 'Select All'}
              </span>
            </button>
            
            <p className="text-sm text-gray-600">
              Showing {filteredCandidates.length} of {pagination.totalCandidates} candidates
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {(filters.search || filters.location || filters.experience || filters.skills) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filters
              </button>
            )}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Filtered</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Candidates Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
      >
        {filteredCandidates.map((candidate, index) => (
          <motion.div
            key={candidate._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className={`card hover:shadow-hover transition-all duration-300 ${
              selectedCandidates.includes(candidate._id) ? 'ring-2 ring-blue-500' : ''
            }`}
          >
            {/* Selection Checkbox */}
            <div className="flex items-start justify-between mb-4">
              <button
                onClick={() => handleCandidateSelect(candidate._id)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  selectedCandidates.includes(candidate._id)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 hover:border-blue-500'
                }`}
              >
                {selectedCandidates.includes(candidate._id) && (
                  <CheckSquare className="w-3 h-3" />
                )}
              </button>
            </div>

            {/* Candidate Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                {candidate.profilePicture ? (
                  <img
                    src={candidate.profilePicture}
                    alt={`${candidate.firstName} ${candidate.lastName}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-medium text-blue-700">
                    {candidate.firstName?.charAt(0)}{candidate.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  {candidate.firstName?.charAt(0)}{candidate.lastName?.charAt(0)}
                </h3>
                <p className="text-sm text-gray-600">***@***.***</p>
              </div>
            </div>

            {/* Professional Info */}
            <div className="mb-4">
              {candidate.profile?.currentPosition && (
                <p className="font-medium text-gray-900 mb-1">
                  {candidate.profile.currentPosition}
                </p>
              )}
              {candidate.location && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4 mr-1" />
                  {formatLocation(candidate.location)}
                </div>
              )}
              <div className="flex items-center text-sm text-gray-600">
                <Briefcase className="w-4 h-4 mr-1" />
                {getExperienceLevel(candidate.totalExperience || 0)} • {candidate.totalExperience || 0} years
              </div>
            </div>

            {/* Skills Preview */}
            {candidate.skills && candidate.skills.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {candidate.skills.slice(0, 3).map((skill, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800"
                    >
                      {skill.name}
                    </span>
                  ))}
                  {candidate.skills.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700">
                      +{candidate.skills.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Bio Preview */}
            {candidate.profile?.bio && (
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {candidate.profile.bio}
              </p>
            )}

            {/* WA & Location stacked */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-gray-700">
                    WA: {candidate.workAuthorization}
                  </span>
                  {candidate.workAuthorizationDetails && (
                    <span className="text-sm font-medium text-gray-700">- {candidate.workAuthorizationDetails}</span>
                  )}
                </div>
                <div className="flex items-center space-x-1 mt-1">
                  <span className="text-sm font-medium text-gray-700">
                    Location: {formatLocation(candidate.address)}
                  </span>
                </div>
              </div>

              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                {new Date(candidate.updatedAt).toLocaleDateString()}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setSelectedCandidate(candidate);
                  setShowProfile(true);
                }}
                className="btn-secondary text-sm py-2 px-4 flex items-center"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Profile
              </button>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setSelectedCandidates([candidate._id]);
                    setShowBulkRequest(true);
                  }}
                  className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50"
                  title="Request Contact"
                >
                  <Mail className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {filteredCandidates.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="empty-state"
        >
          <Users className="empty-state-icon" />
          <h3 className="empty-state-title">
            {filters.search || filters.location || filters.experience || filters.skills
              ? 'No candidates found'
              : 'No candidates available'
            }
          </h3>
          <p className="empty-state-description">
            {filters.search || filters.location || filters.experience || filters.skills
              ? 'Try adjusting your search criteria or filters'
              : 'Candidates will appear here as they join the platform'
            }
          </p>
          {(filters.search || filters.location || filters.experience || filters.skills) && (
            <button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pagination"
        >
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {[...Array(pagination.totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-item ${
                  pagination.currentPage === page ? 'active' : ''
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </motion.div>
      )}

      {/* Candidate Profile Modal */}
      {showProfile && selectedCandidate && (
        <div className="modal-overlay" onClick={() => setShowProfile(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="text-xl font-semibold text-gray-900">
                Candidate Profile
              </h3>
              <button
                onClick={() => setShowProfile(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* Basic Info */}
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  {selectedCandidate.profilePicture ? (
                    <img
                      src={selectedCandidate.profilePicture}
                      alt={`${selectedCandidate.firstName} ${selectedCandidate.lastName}`}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-medium text-blue-700">
                      {selectedCandidate.firstName?.charAt(0)}{selectedCandidate.lastName?.charAt(0)}
                    </span>
                  )}
                </div>
                
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedCandidate.firstName?.charAt(0)}{selectedCandidate.lastName?.charAt(0)}
                  </h4>
                  {selectedCandidate.profile?.currentPosition && (
                    <p className="text-lg text-gray-700 mb-2">
                      {selectedCandidate.profile.currentPosition}
                    </p>
                  )}
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      ***@***.***
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 mr-1" />
                      ***-***-****
                    </div>
                    {selectedCandidate.location && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {formatLocation(selectedCandidate.location)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedCandidate.profile?.bio && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">About</h5>
                  <p className="text-gray-700">{selectedCandidate.profile.bio}</p>
                </div>
              )}

              {/* Skills */}
              {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Skills</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {typeof skill === 'string' ? skill : skill.name}
                        {skill.level && (
                          <span className="ml-1 text-xs text-blue-600">({skill.level})</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {selectedCandidate.workExperience && selectedCandidate.workExperience.length > 0 && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Work Experience</h5>
                  <div className="space-y-4">
                    {selectedCandidate.workExperience.map((exp, idx) => (
                      <div key={idx} className="border-l-2 border-blue-200 pl-4">
                        <h6 className="font-medium text-gray-900">{exp.jobTitle || exp.position}</h6>
                        <p className="text-gray-700">{exp.company}</p>
                        <p className="text-sm text-gray-600">
                          {exp.startDate} - {exp.currentJob ? 'Present' : exp.endDate}
                        </p>
                        {exp.description && (
                          <p className="text-sm text-gray-600 mt-2">{exp.description}</p>
                        )}
                        {exp.achievements && exp.achievements.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-gray-700">Key Achievements:</p>
                            <ul className="text-sm text-gray-600 list-disc list-inside">
                              {exp.achievements.map((achievement, achIdx) => (
                                <li key={achIdx}>{achievement}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              {selectedCandidate.projects && selectedCandidate.projects.length > 0 && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Projects</h5>
                  <div className="space-y-4">
                    {selectedCandidate.projects.map((project, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h6 className="font-medium text-gray-900">{project.title}</h6>
                            {project.description && (
                              <p className="text-gray-700 mt-1">{project.description}</p>
                            )}
                            {project.technologies && project.technologies.length > 0 && (
                              <div className="mt-2">
                                <p className="text-sm font-medium text-gray-700">Technologies:</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {project.technologies.map((tech, techIdx) => (
                                    <span
                                      key={techIdx}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800"
                                    >
                                      {tech}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {project.duration && (
                              <p className="text-sm text-gray-600 mt-2">Duration: {project.duration}</p>
                            )}
                          </div>
                          {project.link && (
                            <a
                              href={project.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 text-blue-600 hover:text-blue-800 text-sm"
                            >
                              View Project →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {selectedCandidate.education && selectedCandidate.education.length > 0 && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Education</h5>
                  <div className="space-y-3">
                    {selectedCandidate.education.map((edu, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <h6 className="font-medium text-gray-900">{edu.degree}</h6>
                        <p className="text-gray-700">{edu.institution}</p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-sm text-gray-600">{edu.graduationYear || edu.year}</p>
                          {edu.gpa && (
                            <p className="text-sm text-gray-600">GPA: {edu.gpa}</p>
                          )}
                        </div>
                        {edu.fieldOfStudy && (
                          <p className="text-sm text-gray-600 mt-1">Field: {edu.fieldOfStudy}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Work Authorization */}
              {selectedCandidate.workAuthorization && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Work Authorization</h5>
                  <p className="text-gray-700">{selectedCandidate.workAuthorization}</p>
                  {selectedCandidate.workAuthorizationDetails && (
                    <p className="text-gray-700">{selectedCandidate.workAuthorizationDetails}</p>
                  )}
                </div>
              )}  
               
              {/* Location */}
              {selectedCandidate.address && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Location</h5>
                  <p className="text-gray-700">{formatLocation(selectedCandidate.address)}</p>
                </div>
              )}  
              {/* Job Preferences */}
              {selectedCandidate.jobPreferences && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Job Preferences</h5>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {selectedCandidate.jobPreferences.preferredJobTypes && selectedCandidate.jobPreferences.preferredJobTypes.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Preferred Job Types:</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedCandidate.jobPreferences.preferredJobTypes.map((type, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-100 text-purple-800"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Remote Work:</p>
                        <p className="text-sm text-gray-600">
                          {selectedCandidate.jobPreferences.remoteWork ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Willing to Relocate:</p>
                        <p className="text-sm text-gray-600">
                          {selectedCandidate.jobPreferences.willingToRelocate ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>

                    {selectedCandidate.jobPreferences.preferredLocations && selectedCandidate.jobPreferences.preferredLocations.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Preferred Locations:</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedCandidate.jobPreferences.preferredLocations.map((location, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded text-xs bg-orange-100 text-orange-800"
                            >
                              {location}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCandidate.jobPreferences.expectedSalary && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Expected Salary:</p>
                        <p className="text-sm text-gray-600">
                          ${selectedCandidate.jobPreferences.expectedSalary.min} - ${selectedCandidate.jobPreferences.expectedSalary.max} {selectedCandidate.jobPreferences.expectedSalary.currency || 'USD'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Total Experience */}
              {selectedCandidate.totalExperience !== undefined && (
                <div>
                  <h5 className="text-lg font-medium text-gray-900 mb-3">Experience Summary</h5>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-blue-800">
                      <span className="font-medium">{selectedCandidate.totalExperience} years</span> of total experience
                      <span className="text-blue-600 ml-2">({getExperienceLevel(selectedCandidate.totalExperience)})</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowProfile(false)}
                className="btn-secondary"
              >
                Close
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    // Add to bulk request list
                    if (!selectedCandidates.includes(selectedCandidate._id)) {
                      setSelectedCandidates(prev => [...prev, selectedCandidate._id]);
                      toast.success('Candidate added to bulk request list');
                    } else {
                      toast.info('Candidate already in bulk request list');
                    }
                  }}
                  className="btn-secondary flex items-center"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Add to Bulk Request
                </button>
                <button
                  onClick={() => {
                    setSelectedCandidates([selectedCandidate._id]);
                    setShowProfile(false);
                    setShowBulkRequest(true);
                  }}
                  className="btn-primary flex items-center"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Individual Request
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Request Modal */}
      {showBulkRequest && (
        <div className="modal-overlay" onClick={() => setShowBulkRequest(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="text-xl font-semibold text-gray-900">
                Request Candidate Contact Details
              </h3>
              <button
                onClick={() => setShowBulkRequest(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">
                      Contact Details Request
                    </h4>
                    <p className="text-sm text-blue-700 mt-1">
                      This request will be sent to the admin for approval. Once approved, you'll receive contact details for the selected {selectedCandidates.length} candidate{selectedCandidates.length > 1 ? 's' : ''}.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label">Collection Name *</label>
                <input
                  type="text"
                  value={bulkRequestData.title}
                  onChange={(e) => setBulkRequestData({ ...bulkRequestData, title: e.target.value })}
                  className="input-field"
                  placeholder="e.g. Frontend Rockstar Shortlist"
                />
              </div>

              <div>
                <label className="form-label">Related Job Position (Optional)</label>
                <select
                  value={bulkRequestData.jobId}
                  onChange={(e) => setBulkRequestData({ ...bulkRequestData, jobId: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select a job position</option>
                  {jobs.map(job => (
                    <option key={job._id} value={job._id}>
                      {job.title} - {job.location?.city ? `${job.location.city}, ${job.location.state}` : 'Location not specified'}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Linking to a job position helps admin understand the context of your request
                </p>
              </div>

              <div>
                <label className="form-label">Business Justification *</label>
                <textarea
                  value={bulkRequestData.message}
                  onChange={(e) => setBulkRequestData({ ...bulkRequestData, message: e.target.value })}
                  rows={6}
                  className="input-field"
                  placeholder="Please explain why you need contact details for these candidates. Include information about the role, urgency, client requirements, etc..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  Provide a clear business justification to help admin approve your request quickly
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowBulkRequest(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkRequest}
                className="btn-primary flex items-center"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Request to Admin
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CandidateSearch; 